import React, { memo } from 'react';
import { theme } from '../../theme';
import { MAX_CONTAINER_WIDTH, PAGE_PADDING } from '../../utils/constants';
import { useBreakpoint } from '../../hooks/useBreakpoint';

interface PageWrapperProps {
  /** Contenido a renderizar dentro del wrapper */
  children: React.ReactNode;
  /** Ancho máximo del contenedor (default: 1400px) */
  maxWidth?: string;
  /** Padding personalizado (default: 24px) */
  padding?: string;
  /** Clase CSS adicional */
  className?: string;
}

/**
 * Componente wrapper reutilizable para páginas
 * Aplica estilos consistentes de layout, padding y fondo
 * 
 * Optimizado con React.memo para evitar re-renders innecesarios
 * 
 * @example
 * <PageWrapper maxWidth="1200px">
 *   <MiComponente />
 * </PageWrapper>
 */
export const PageWrapper = memo<PageWrapperProps>(({
  children,
  maxWidth = MAX_CONTAINER_WIDTH,
  padding,
  className = '',
}) => {
  const { isMobile, isTablet } = useBreakpoint();
  const defaultPadding = isMobile ? '8px' : isTablet ? '12px' : PAGE_PADDING;
  const effectivePadding = padding ?? defaultPadding;

  return (
    <div
      className={className}
      style={{
        padding: effectivePadding,
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start'
      }}
    >
      <div style={{
        width: '100%',
        maxWidth,
        margin: '0 auto'
      }}>
        {children}
      </div>
    </div>
  );
});

PageWrapper.displayName = 'PageWrapper';

